import type { ThemeStructure } from "@dit/ui-components.react.theme/dist/utils";
import { DefaultTheme } from "styled-components";
import { getTheme, getStyles } from "@app/services/openapi/userexperience/client";
import { client as userExperienceClient } from "@app/services/openapi/userexperience/client/client.gen";
import { deepMergeArrays } from "@app/core/utilities/array";

const CACHE_KEY_PREFIX = "ux-theme";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CacheEntry<T> {
    value: T;
    expiresAt: number;
}

function buildCacheKey(tenantKey: string, applicationName: string, themeMode?: string): string {
    return `${CACHE_KEY_PREFIX}:${tenantKey}:${applicationName}:${themeMode ?? "default"}`;
}

function isThemeStructure(value: unknown): value is ThemeStructure {
    return typeof value === "object" && value !== null && "brand" in value;
}

function readCache<T>(key: string): T | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) {
            return null;
        }

        const entry: CacheEntry<T> = JSON.parse(raw);
        if (Date.now() > entry.expiresAt) {
            localStorage.removeItem(key);
            return null;
        }
        return entry.value;
    } catch {
        return null;
    }
}

function writeCache<T>(key: string, value: T): void {
    try {
        const entry: CacheEntry<T> = { value, expiresAt: Date.now() + CACHE_TTL_MS };
        localStorage.setItem(key, JSON.stringify(entry));
    } catch {
        // localStorage full or unavailable
    }
}

export async function fetchTenantTheme(
    tenantKey: string,
    applicationName: string,
    fallbackTheme: ThemeStructure,
    themeMode?: string
): Promise<ThemeStructure> {
    const cacheKey = buildCacheKey(tenantKey, applicationName, themeMode);
    const cached = readCache<ThemeStructure>(cacheKey);
    if (cached) {
        return cached;
    }

    try {
        const response = await getTheme({
            client: userExperienceClient,
            query: {
                applicationName,
                ...(themeMode ? { themeMode } : {}),
            },
            headers: {
                "X-Tenant-Name": tenantKey,
            },
        });

        const themeJson = response.data?.theme;

        if (!themeJson) {
            console.warn("[UX Service] Theme API returned empty theme, using fallback");
            return fallbackTheme;
        }

        const parsed: unknown = JSON.parse(themeJson);

        if (!isThemeStructure(parsed)) {
            console.warn(
                typeof parsed !== "object" || parsed === null
                    ? "[UX Service] Theme API returned non-object theme, using fallback"
                    : "[UX Service] Theme API returned object missing 'brand' key, using fallback"
            );
            return fallbackTheme;
        }

        writeCache(cacheKey, parsed);
        return parsed;
    } catch (error) {
        console.warn("[UX Service] Failed to fetch tenant theme, using fallback", error);
        return fallbackTheme;
    }
}

// v1 styled-components theme — temporary until full migration to v2
export async function fetchV1TenantTheme(
    tenantKey: string,
    applicationKey: string,
    language: string,
    fallbackTheme: DefaultTheme
): Promise<DefaultTheme> {
    const cacheKey = `ux-v1-theme:${tenantKey}:${applicationKey}:${language}`;
    const cached = readCache<DefaultTheme>(cacheKey);
    if (cached) {
        return cached;
    }

    try {
        const response = await getStyles({
            client: userExperienceClient,
            query: { applicationKey, lang: language },
            headers: { "X-Tenant-Name": tenantKey },
        });

        if (!response.data) {
            console.warn("[UX Service] V1 theme API returned empty response, using fallback");
            return fallbackTheme;
        }

        const merged = mergeV1Theme(fallbackTheme, response.data as ConfigUiDefinition);
        writeCache(cacheKey, merged);
        return merged;
    } catch (error) {
        console.warn("[UX Service] Failed to fetch V1 tenant theme, using fallback", error);
        return fallbackTheme;
    }
}

function mergeV1Theme(globalTheme: DefaultTheme, style: ConfigUiDefinition): DefaultTheme {
    return {
        ...globalTheme,
        tokens: {
            ...globalTheme.tokens,
            images: deepMergeArrays(globalTheme.tokens.images, style.images ?? [], "imageKey"),
            fonts: deepMergeArrays(globalTheme.tokens.fonts, style.fonts ?? [], "fontKey"),
            radius: deepMergeArrays(globalTheme.tokens.radius, style.radius ?? [], "radiusKey"),
            colors: deepMergeArrays(globalTheme.tokens.colors, style.colors ?? [], "colorKey"),
            customStyles: deepMergeArrays(globalTheme.tokens.customStyles, style.customStyles ?? [], "customStyleKey"),
        },
    };
}
